import { z } from 'zod';

const OfferStatusSchema = z.enum(['draft', 'published', 'internal', 'closed', 'archived']);

const AvatarSchema = z.object({
  name: z.string(),
  avatarUrl: z.string().optional(),
  initials: z.string().optional(),
});

const PersonSubtitleSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('text'), text: z.string() }),
  z.object({ kind: z.literal('job-ref'), title: z.string(), status: OfferStatusSchema }),
  z.object({ kind: z.literal('jobs-count'), count: z.number().int().nonnegative() }),
]);

const ItemBadgeSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('lead') }),
  z.object({ kind: z.literal('overdue'), days: z.number().int().positive() }),
]);

const CandidateDataSchema = z.object({
  id: z.string(),
  name: z.string(),
  avatarUrl: z.string().optional(),
  initials: z.string().optional(),
  subtitle: PersonSubtitleSchema.optional(),
  badge: ItemBadgeSchema.optional(),
});

const TeamMemberDataSchema = z.object({
  id: z.string(),
  name: z.string(),
  avatarUrl: z.string().optional(),
  initials: z.string().optional(),
  subtitle: PersonSubtitleSchema.optional(),
});

const JobDataSchema = z.object({
  id: z.string(),
  title: z.string(),
  status: OfferStatusSchema,
  candidatesCount: z.number().int().nonnegative().optional(),
  location: z.string().optional(),
});

const TalentPoolDataSchema = z.object({
  id: z.string(),
  title: z.string(),
  status: z.enum(['draft', 'archived']),
  candidatesCount: z.number().int().nonnegative().optional(),
});

const LinkDataSchema = z.object({
  id: z.string(),
  label: z.string(),
});

const EventDataSchema = z.object({
  id: z.string(),
  startsAt: z.string().datetime(),
  duration: z.number().int().positive().optional(),
  candidate: AvatarSchema.optional(),
  assignedJob: z.object({ title: z.string(), status: OfferStatusSchema }).optional(),
});

const EvaluationDataSchema = z.object({
  id: z.string(),
  template: z.string(),
  candidate: AvatarSchema,
  scope: z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('job'), title: z.string(), status: OfferStatusSchema }),
    z.object({ kind: z.literal('multi-jobs'), count: z.number().int().positive() }),
  ]),
});

const TaskDataSchema = z.object({
  id: z.string(),
  title: z.string(),
  dueDate: z.string().datetime().optional(),
  completed: z.boolean().optional(),
  admins: z.array(AvatarSchema).max(10).optional(),
  triggered: z.boolean().optional(),
});

const RequisitionDataSchema = z.object({
  id: z.string(),
  title: z.string(),
  hiresCount: z.number().int().nonnegative(),
  openingsCount: z.number().int().positive(),
  status: z.enum(['draft', 'pending', 'rejected', 'approved', 'filled']),
  departmentName: z.string().optional(),
});

export const ListItemSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('candidate'), data: CandidateDataSchema }),
  z.object({ kind: z.literal('team-member'), data: TeamMemberDataSchema }),
  z.object({ kind: z.literal('job'), data: JobDataSchema }),
  z.object({ kind: z.literal('talent-pool'), data: TalentPoolDataSchema }),
  z.object({ kind: z.literal('link'), data: LinkDataSchema }),
  z.object({ kind: z.literal('event'), data: EventDataSchema }),
  z.object({ kind: z.literal('evaluation'), data: EvaluationDataSchema }),
  z.object({ kind: z.literal('task'), data: TaskDataSchema }),
  z.object({ kind: z.literal('requisition'), data: RequisitionDataSchema }),
]);
